import * as React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import RoleRoute from "@/components/RoleRoute";
import { RouteLoaderSkeleton } from "@/components/skeletons";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { AdminRoutes } from "./routes/admin";
import { SEORoutes } from "./routes/seo";

// Critical routes - Load immediately (homepage only)

/**
 * Wrap React.lazy with a single automatic retry (1.5 s delay) to handle
 * transient chunk-fetch failures on slow/flaky mobile connections.
 */
const lazyWithRetry = <T extends React.ComponentType<unknown>>(
  fn: () => Promise<{ default: T }>
) =>
  React.lazy(() =>
    fn().catch(
      () =>
        new Promise<{ default: T }>((resolve, reject) =>
          setTimeout(() => fn().then(resolve).catch(reject), 1500)
        )
    )
  );

// Lazy load non-critical routes for better performance
const SearchResults = lazyWithRetry(() => import("./pages/cars/SearchResults"));
const Login = lazyWithRetry(() => import("./pages/auth/Login"));
const Register = lazyWithRetry(() => import("./pages/auth/Register"));
const CarDetail = lazyWithRetry(() => import("./pages/cars/CarDetail"));
const ComparePage = lazyWithRetry(() => import("./pages/cars/ComparePage"));
const ReservationPage = lazyWithRetry(() => import("./pages/cars/ReservationPage"));
const ForgotPassword = lazyWithRetry(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/auth/ResetPassword"));
const ChangePassword = lazyWithRetry(() => import("./pages/auth/ChangePassword"));
const VerifyEmail = lazyWithRetry(() => import("./pages/auth/VerifyEmail"));
const EmailVerified = lazyWithRetry(() => import("./pages/auth/EmailVerified"));
const GoogleCallback = lazyWithRetry(() => import("./pages/auth/GoogleCallback"));
const MobileAuthCallback = lazyWithRetry(() => import("./pages/auth/MobileAuthCallback"));
const AuthConfirm = lazyWithRetry(() => import("./pages/auth/AuthConfirm"));
const CompleteProfile = lazyWithRetry(() => import("./pages/auth/CompleteProfile"));
const DispatchDecisionPage = lazyWithRetry(() => import("./pages/dispatch/DispatchDecisionPage"));
const DispatchClaimPage = lazyWithRetry(() => import("./pages/dispatch/DispatchClaimPage"));
const FleetMagicAccessPage = lazyWithRetry(() => import("./pages/agency/FleetMagicAccessPage"));

// Dashboard routes - Lazy loaded (only accessed when logged in)
const OwnerDashboard = lazyWithRetry(() => import("./pages/dashboard/OwnerDashboard"));
const RenterDashboard = lazyWithRetry(() => import("./pages/dashboard/RenterDashboard"));
const Profile = lazyWithRetry(() => import("./pages/profile/Profile"));
const OwnerVehicles = lazyWithRetry(() => import("./pages/dashboard/OwnerVehicles"));
const OwnerFleetAvailability = lazyWithRetry(() => import("./pages/dashboard/OwnerFleetAvailability"));
const OwnerBookingsDashboard = lazyWithRetry(() => import("./pages/dashboard/OwnerBookingsDashboard"));
const RenterBookingsDashboard = lazyWithRetry(() => import("./pages/dashboard/RenterBookingsDashboard"));
const RenterCancellationsPage = lazyWithRetry(() => import("./pages/dashboard/RenterCancellationsPage"));
const AgencyReviews = lazyWithRetry(() => import("./pages/dashboard/AgencyReviews"));
const OwnerRevenueDashboard = lazyWithRetry(() => import("./pages/dashboard/OwnerRevenueDashboard"));
const AgencyRevenueDashboard = lazyWithRetry(() => import("./pages/dashboard/owner/AgencyRevenueDashboard"));
const OwnerCancellationsDashboard = lazyWithRetry(() => import("./pages/dashboard/OwnerCancellationsDashboard"));
const OwnerClaimsDashboard = lazyWithRetry(() => import("./pages/dashboard/OwnerClaimsDashboard"));
const OwnerBankDetailsPage = lazyWithRetry(() => import("./pages/dashboard/owner/OwnerBankDetailsPage.tsx"));

// Vehicle management - Lazy loaded
const AddCar = lazyWithRetry(() => import("./pages/cars/AddCarPage"));
const EditCar = lazyWithRetry(() => import("./pages/cars/EditCar"));
const VehicleAvailabilityPage = lazyWithRetry(() => import("./pages/cars/VehicleAvailabilityPage"));
const VehicleStats = lazyWithRetry(() => import("./pages/cars/VehicleStats"));
const VehicleReviews = lazyWithRetry(() => import("./pages/cars/VehicleReviews"));

// Booking routes - Lazy loaded
const BookingDetailsPage = lazyWithRetry(() => import("./pages/bookings/BookingDetailsPage"));
const BookingConfirmation = lazyWithRetry(() => import("./pages/bookings/BookingConfirmation"));
const SubmitReview = lazyWithRetry(() => import("./pages/bookings/SubmitReview"));
const CheckOutPage = lazyWithRetry(() => import("./pages/bookings/CheckOutPage"));
const DocumentPage = lazyWithRetry(() => import("./pages/bookings/DocumentPage"));
const InvoicePage = lazyWithRetry(() => import("./pages/bookings/InvoicePage"));
const ReceiptPage = lazyWithRetry(() => import("./pages/bookings/ReceiptPage"));
const ContractPage = lazyWithRetry(() => import("./pages/bookings/ContractPage"));
const CancelBookingPage = lazyWithRetry(() => import("./pages/bookings/CancelBookingPage"));
const DamageReportPage = lazyWithRetry(() => import("./pages/bookings/DamageReportPage"));
const ClaimResponsePage = lazyWithRetry(() => import("./pages/bookings/ClaimResponsePage"));
const ClaimDetailPage = lazyWithRetry(() => import("./pages/bookings/ClaimDetailPage"));

// Payment routes - Lazy loaded
const PaymentSuccess = lazyWithRetry(() => import("./pages/payment/success"));
const PaymentFailure = lazyWithRetry(() => import("./pages/payment/failure"));
const PaymentCancel = lazyWithRetry(() => import("./pages/payment/cancel"));

// Guest routes - Lazy loaded
const GuestUploadDocuments = lazyWithRetry(() => import("./pages/guest/UploadDocuments"));
const GuestBookings = lazyWithRetry(() => import("./pages/guest/GuestBookings"));
const GuestBookingApproved = lazyWithRetry(() => import("./pages/guest/GuestBookingApproved"));

// Other pages - Lazy loaded
const HowItWorks = lazyWithRetry(() => import("./pages/how-it-works/HowItWorks"));
const Contact = lazyWithRetry(() => import("./pages/contact/Contact"));
const AgenciesLanding = lazyWithRetry(() => import("./pages/agencies/AgenciesLanding"));
const AgencyProfile = lazyWithRetry(() => import("./pages/agencies/AgencyProfile"));
const Legal = lazyWithRetry(() => import("./pages/legal/Legal"));
const Privacy = lazyWithRetry(() => import("./pages/legal/Privacy"));
const TermsOfService = lazyWithRetry(() => import("./pages/legal/TermsOfService"));
const CancellationPolicy = lazyWithRetry(() => import("./pages/legal/CancellationPolicy"));
const Insurance = lazyWithRetry(() => import("./pages/legal/Insurance"));
const OwnerTools = lazyWithRetry(() => import("./pages/owner/OwnerTools"));
const Notifications = lazyWithRetry(() => import("./pages/notifications/Notifications"));
const BeforeOwner = lazyWithRetry(() => import("@/pages/owner/BeforeOwner"));
const Favorites = lazyWithRetry(() => import("@/pages/favorites/Favorites"));
const About = lazyWithRetry(() => import("@/pages/about/About"));
const Blog = lazyWithRetry(() => import("@/pages/blog/Blog"));
const BlogPost = lazyWithRetry(() => import("@/pages/blog/BlogPost"));
const Rent = lazyWithRetry(() => import("@/pages/rent/Rent"));
const Help = lazyWithRetry(() => import("@/pages/help/Help"));
const Guide = lazyWithRetry(() => import("@/pages/guide/Guide"));
const FaqPage = lazyWithRetry(() => import("@/pages/faq/Faq"));
const Emergency = lazyWithRetry(() => import("@/pages/emergency/Emergency"));
const MessagesList = lazyWithRetry(() => import("@/pages/messaging/MessagesList"));
const MessageThreadPage = lazyWithRetry(() => import("@/pages/messaging/MessageThread"));
const CityLandingPage = lazyWithRetry(() => import("@/pages/cities/CityLandingPage"));
const LocationVoitureCasablanca150Dh = lazyWithRetry(() => import("@/pages/cities/LocationVoitureCasablanca150Dh.tsx"));
const IntentLandingPage = lazyWithRetry(() => import("@/pages/cities/IntentLandingPage"));
const CategoryLandingPage = lazyWithRetry(() => import("@/pages/categories/CategoryLandingPage"));
const DocumentsVerificationRedirect = lazyWithRetry(
  () => import("./pages/documents/DocumentsVerificationRedirect")
);
const TenantVerificationHub = lazyWithRetry(() => import("./pages/verify/TenantVerificationHub"));
const HotelPartnerLandingPage = lazyWithRetry(() => import("@/pages/hotel-partners/HotelPartnerLandingPage"));

// Composant de redirection pour la route alternative avec tiret
// Redirige /location-voiture-casablanca vers /location-voiture/casablanca
const CityLandingPageRedirect = () => {
  const params = useParams();
  const city = params.city as string | undefined;
  const normalizedSlug = city?.toLowerCase() || "";
  return <Navigate to={`/location-voiture/${normalizedSlug}`} replace />;
};

function AppRoutes() {
  return (
    <React.Suspense fallback={<RouteLoaderSkeleton />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/cars/compare" element={<Navigate to="/compare" replace />} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/register" element={<Register />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/change-password" element={<ChangePassword />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/email-verified" element={<EmailVerified />} />
        <Route path="/auth/callback" element={<GoogleCallback />} />
        <Route path="/auth/mobile-callback" element={<MobileAuthCallback />} />
        <Route path="/auth/confirm" element={<AuthConfirm />} />
        <Route path="/auth/complete-profile" element={<CompleteProfile />} />
        <Route
          path="/dashboard/renter"
          element={
            <RoleRoute allowedRoles={["renter", "admin"]}>
              <RenterDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/vehicles"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerVehicles />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/fleet"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerFleetAvailability />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/bookings"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerBookingsDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/reviews"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <AgencyReviews />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/revenue"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <AgencyRevenueDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/revenue-old"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerRevenueDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/cancellations"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerCancellationsDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/claims"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerClaimsDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/owner/bank-details"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <OwnerBankDetailsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/renter/bookings"
          element={
            <RoleRoute allowedRoles={["renter", "admin"]}>
              <RenterBookingsDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="/dashboard/renter/cancellations"
          element={
            <RoleRoute allowedRoles={["renter", "admin"]}>
              <RenterCancellationsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <Profile />
            </RoleRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <Notifications />
            </RoleRoute>
          }
        />
        <Route path="/favorites" element={<Favorites />} />
        <Route
          path="/cars/add"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <AddCar />
            </RoleRoute>
          }
        />
        <Route
          path="/cars/:id/edit"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <EditCar />
            </RoleRoute>
          }
        />
        <Route
          path="/cars/:id/availability"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <VehicleAvailabilityPage />
            </RoleRoute>
          }
        />
        <Route
          path="/cars/:id/stats"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <VehicleStats />
            </RoleRoute>
          }
        />
        <Route path="/cars/:id/reviews" element={<VehicleReviews />} />
        <Route path="/search" element={<SearchResults />} />
        {/* Compat legacy: serve same page to avoid mobile redirect loops */}
        <Route path="/cars/search" element={<SearchResults />} />
        <Route path="/cars/:id" element={<CarDetail />} />
        {/* Page de réservation - accessible à tous, la page gère l'authentification */}
        <Route path="/cars/:id/reserve" element={<ReservationPage />} />
        <Route path="/before-owner" element={<BeforeOwner />} />
        {/* Redirection 301 de /become-owner vers /agences */}
        <Route path="/become-owner" element={<Navigate to="/agences" replace />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/legal/insurance" element={<Insurance />} />

        <Route path="/owner/tools" element={<OwnerTools />} />
        {/* Compat : /documents/verification redirige les renters vers /verify/tenant */}
        <Route
          path="/documents/verification"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <DocumentsVerificationRedirect />
            </RoleRoute>
          }
        />
        <Route
          path="/verify/tenant"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <TenantVerificationHub />
            </RoleRoute>
          }
        />
        <Route
          path="/verify/host"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <Navigate to="/profile" replace />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <BookingDetailsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/review"
          element={
            <RoleRoute allowedRoles={["renter", "admin"]}>
              <SubmitReview />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/document/:type"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <DocumentPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/check-out"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <CheckOutPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/invoice"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <InvoicePage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/receipt"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <ReceiptPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/contract"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <ContractPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/cancel"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <CancelBookingPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/damage-report"
          element={
            <RoleRoute allowedRoles={["owner", "admin"]}>
              <DamageReportPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/claims/:claimId"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <ClaimDetailPage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/claims/:claimId/respond"
          element={
            <RoleRoute allowedRoles={["renter", "admin"]}>
              <ClaimResponsePage />
            </RoleRoute>
          }
        />
        <Route
          path="/bookings/:id/confirm"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <BookingConfirmation />
            </RoleRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <MessagesList />
            </RoleRoute>
          }
        />
        <Route
          path="/messages/:threadId"
          element={
            <RoleRoute allowedRoles={["renter", "owner", "admin"]}>
              <MessageThreadPage />
            </RoleRoute>
          }
        />
        {/* Admin — fragment of <Route> elements (RR6 direct children only) */}
        {AdminRoutes()}
        <Route path="/about" element={<About />} />
        <Route path="/a-propos" element={<Navigate to="/about" replace />} />
        <Route path="/mentions-legales" element={<Navigate to="/legal" replace />} />
        <Route path="/confidentialite" element={<Navigate to="/legal/privacy" replace />} />
        <Route path="/conditions-generales" element={<Navigate to="/legal/terms" replace />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        {/* City SEO Landing Pages */}
        {/* Page stratégique "location voiture casablanca 150 dh" - route exacte avant location-voiture-:city */}
        <Route path="/location-voiture-casablanca-150-dh" element={<LocationVoitureCasablanca150Dh />} />
        {/* Pages argent (intent commercial) */}
        <Route path="/location-voiture-casablanca-pas-cher" element={<IntentLandingPage />} />
        <Route path="/location-voiture-rabat-pas-cher" element={<IntentLandingPage />} />
        <Route path="/location-voiture-marrakech-pas-cher" element={<IntentLandingPage />} />
        <Route path="/location-voiture-tanger-pas-cher" element={<IntentLandingPage />} />
        <Route path="/location-voiture-aeroport-casablanca" element={<IntentLandingPage />} />
        <Route path="/location-voiture-aeroport-marrakech" element={<IntentLandingPage />} />
        <Route path="/location-voiture-150-dh" element={<IntentLandingPage />} />
        <Route path="/location-voiture-sans-caution-maroc" element={<IntentLandingPage />} />
        <Route path="/location-voiture-longue-duree-maroc" element={<IntentLandingPage />} />
        {/* Route principale (canonique) - format: /location-voiture/casablanca */}
        <Route path="/location-voiture/:city" element={<CityLandingPage />} />
        {/* Route alternative avec tiret (redirection 301 vers format canonique) - format: /location-voiture-casablanca */}
        <Route path="/location-voiture-:city" element={<CityLandingPageRedirect />} />
        {/* Pages catégories véhicules */}
        <Route path="/categories/:category" element={<CategoryLandingPage />} />
        <Route path="/hotel-partners/:slug" element={<HotelPartnerLandingPage />} />
        <Route path="/rent" element={<Rent />} />
        <Route path="/help" element={<Help />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/emergency" element={<Emergency />} />
        <Route path="/agences/:id" element={<AgencyProfile />} />
        <Route path="/agences" element={<AgenciesLanding />} />
        {/* Legacy/alt slugs: keep agency landing reachable, avoid 404 from old links */}
        <Route path="/agencies" element={<Navigate to="/agences" replace />} />
        <Route path="/pour-agences" element={<Navigate to="/agences" replace />} />
        <Route path="/pour-agence" element={<Navigate to="/agences" replace />} />
        <Route path="/dispatch" element={<DispatchDecisionPage />} />
        <Route path="/dispatch/claim" element={<DispatchClaimPage />} />
        <Route path="/agency/fleet" element={<FleetMagicAccessPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failure" element={<PaymentFailure />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        {/* Guest routes - accessible without authentication */}
        <Route path="/guest/bookings" element={<GuestBookings />} />
        <Route path="/guest/booking/:bookingId/upload-documents/:guestUserId" element={<GuestUploadDocuments />} />
        <Route path="/guest/booking/:bookingId/approved/:guestUserId" element={<GuestBookingApproved />} />
        {/* Promo Eid - redirection /eid → /search avec UTM */}
        <Route
          path="/eid"
          element={
            <Navigate
              to="/search?utm_source=Facebook&utm_medium=organic_group&utm_campaign=promo_eid_10"
              replace
            />
          }
        />
        {/* SEO Programmatic Routes (Level 1-5) */}
        {SEORoutes()}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </React.Suspense>
  );
}

export default AppRoutes;
